import Box from '@material-ui/core/Box';
import { useLegalEntitiesQuery } from 'organization/hooks';
import { getLegalEntitiesStates } from 'organization/Utility/helper';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MultiChoiceField, {
  getDynamicOptionWidth,
} from 'styleguide/form/MultipleChoice';
import AsynchronousLayout from 'styleguide/layout/Asynchronous';
import FieldSet from 'styleguide/layout/FieldSet';
import FilterHeader from 'styleguide/layout/FilterHeader';
import FormLayout from 'styleguide/layout/Form';

const HOME_HEALTH_STATUSES: Record<string, string> = {
  true: 'Active',
  false: 'Inactive',
};

interface FilterOptions {
  active?: string[];
  states?: string[];
}

interface Props {
  values: FilterOptions;
  defaultStates: string[];
  defaultStatuses: string[];
  onApply: (data: FilterOptions) => void;
  onCancel: () => void;
}

const Filters: React.FC<Props> = ({
  values,
  defaultStatuses,
  defaultStates,
  onApply,
  onCancel,
}) => {
  const [homeHealthFilterOptions, setHomeHealthFilterOptions] = useState<FilterOptions>({});

  const {
    entities: homeHealthEntities,
    loading: homeHealthEntityLoading,
    error: homeHealthEntityError,
  } = useLegalEntitiesQuery();

  const homeHealthStates = useMemo(() => {
    return getLegalEntitiesStates(homeHealthEntities);
  }, [homeHealthEntities]);

  const handleHomeHealthStateChange = useCallback(
    (stateValues: string[] | undefined) => {
      setHomeHealthFilterOptions({
        ...homeHealthFilterOptions,
        states: stateValues || [],
      });
    },
    [homeHealthFilterOptions]
  );

  const handleHomeHealthActiveChange = useCallback(
    (activeValues: string[] | undefined) => {
      setHomeHealthFilterOptions({
        ...homeHealthFilterOptions,
        active: activeValues || [],
      });
    },
    [homeHealthFilterOptions]
  );

  const getHomeHealthFilterCount = useCallback(
    (filterKey: string) => {
      switch (filterKey) {
        case 'state':
          return homeHealthFilterOptions?.states?.length
            ? `(${homeHealthFilterOptions?.states?.length})`
            : '';
        case 'active':
          return homeHealthFilterOptions?.active?.length
            ? `(${homeHealthFilterOptions?.active?.length})`
            : '';
      }
    },
    [homeHealthFilterOptions]
  );

  const handleHomeHealthSubmit = useCallback(() => {
    if (homeHealthFilterOptions === values) {
      onCancel();
      return;
    }
  
    onApply(homeHealthFilterOptions);
  }, [homeHealthFilterOptions, values, onApply, onCancel]);
  

  const handleHomeHealthReset = useCallback(() => {
    setHomeHealthFilterOptions({
      ...homeHealthFilterOptions,
      active: defaultStatuses,
      states: defaultStates,
    });
  }, [homeHealthFilterOptions, defaultStatuses, defaultStates]);

  useEffect(() => {
    setHomeHealthFilterOptions(values);
  }, [values]);

  return (
    <AsynchronousLayout data={true} loading={homeHealthEntityLoading} error={homeHealthEntityError}>
      <FormLayout
        onCancel={onCancel}
        onSubmit={handleHomeHealthSubmit}
        submitButtonText="Apply"
        isValid
      >
        <FilterHeader reset={handleHomeHealthReset} />
        <FieldSet
          label={`Home Health Org. Status ${getHomeHealthFilterCount('active')}`}
          action={{
            text: 'Clear',
            variant: 'text',
            onClick: () => {
              setHomeHealthFilterOptions({
                ...homeHealthFilterOptions,
                active: [],
              });
            },
          }}
        >
          <MultiChoiceField
            name="Home Health Org. Status"
            onChange={handleHomeHealthActiveChange}
            onValidate={() => {}}
            options={Object.keys(HOME_HEALTH_STATUSES)}
            value={homeHealthFilterOptions?.active}
            getOptionLabel={(homeHealthOption: string) => HOME_HEALTH_STATUSES[homeHealthOption]}
            getOptionId={(homeHealthOption: string) => homeHealthOption}
            variant="checkboxes"
            getOptionWidth={getDynamicOptionWidth}
          />
        </FieldSet>
        <Box mb={2} />
        <FieldSet
          label={`States ${getHomeHealthFilterCount('state')}`}
          action={{
            text: 'Clear',
            variant: 'text',
            onClick: () => {
              setHomeHealthFilterOptions({
                ...homeHealthFilterOptions,
                states: [],
              });
            },
          }}
        >
          <MultiChoiceField
            name="States"
            onChange={handleHomeHealthStateChange}
            onValidate={() => {}}
            options={homeHealthStates}
            value={homeHealthFilterOptions?.states}
            getOptionLabel={(homeHealthStateOption: string) => homeHealthStateOption}
            getOptionId={(homeHealthStateOption: string) => homeHealthStateOption}
            variant="checkboxes"
            getOptionWidth={getDynamicOptionWidth}
          />
        </FieldSet>
        <Box mb={2} />
      </FormLayout>
    </AsynchronousLayout>
  );
};

export default Filters;
